/**
 * Date Utilities for Reports
 * Common date manipulation functions for report generation
 */

/**
 * Format date for display
 */
export const formatDate = (date, options = {}) => {
  const { 
    includeTime = false, 
    format = 'locale',
    timezone = 'local'
  } = options;

  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime()) || dateObj.getFullYear() < 1970) {
    return '';
  }

  if (format === 'iso') {
    return includeTime ? dateObj.toISOString() : dateObj.toISOString().split('T')[0];
  }

  if (format === 'locale') {
    return includeTime ? dateObj.toLocaleString() : dateObj.toLocaleDateString();
  }

  return dateObj.toString();
};

/**
 * Get date range presets
 */
export const getDateRangePresets = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  
  const yearStart = new Date(today.getFullYear(), 0, 1);

  return {
    today: {
      label: 'Today',
      startDate: formatDate(today, { format: 'iso' }),
      endDate: formatDate(today, { format: 'iso' })
    },
    yesterday: {
      label: 'Yesterday',
      startDate: formatDate(yesterday, { format: 'iso' }),
      endDate: formatDate(yesterday, { format: 'iso' })
    },
    thisWeek: {
      label: 'This Week',
      startDate: formatDate(weekStart, { format: 'iso' }),
      endDate: formatDate(today, { format: 'iso' })
    },
    thisMonth: {
      label: 'This Month',
      startDate: formatDate(monthStart, { format: 'iso' }),
      endDate: formatDate(today, { format: 'iso' })
    },
    lastMonth: {
      label: 'Last Month',
      startDate: formatDate(lastMonthStart, { format: 'iso' }),
      endDate: formatDate(lastMonthEnd, { format: 'iso' })
    },
    thisYear: {
      label: 'This Year',
      startDate: formatDate(yearStart, { format: 'iso' }),
      endDate: formatDate(today, { format: 'iso' })
    },
    last30Days: {
      label: 'Last 30 Days',
      startDate: formatDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), { format: 'iso' }),
      endDate: formatDate(today, { format: 'iso' })
    },
    last90Days: {
      label: 'Last 90 Days',
      startDate: formatDate(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), { format: 'iso' }),
      endDate: formatDate(today, { format: 'iso' })
    }
  };
};

/**
 * Calculate date range duration
 */
export const calculateDateRangeDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { days: 0, weeks: 0, months: 0, isValid: false };
  }

  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  return {
    days: diffDays,
    weeks: diffWeeks,
    months: diffMonths,
    isValid: true,
    isSameDay: start.toDateString() === end.toDateString()
  };
};

/**
 * Validate date range
 */
export const validateDateRange = (startDate, endDate, options = {}) => {
  const { maxDays = 365, minDays = 1 } = options;
  
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Check for valid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }

  // Check date order
  if (start > end) {
    return { isValid: false, error: 'Start date must be before end date' };
  }

  // Check date range length
  const { days } = calculateDateRangeDuration(startDate, endDate);
  
  if (days < minDays) {
    return { isValid: false, error: `Date range must be at least ${minDays} day(s)` };
  }
  
  if (days > maxDays) {
    return { isValid: false, error: `Date range cannot exceed ${maxDays} days` };
  }

  // Check for future dates
  const today = new Date();
  if (start > today) {
    return { isValid: false, error: 'Start date cannot be in the future' };
  }

  return { isValid: true, days };
};

/**
 * Generate date series for charts
 */
export const generateDateSeries = (startDate, endDate, interval = 'day') => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates = [];

  let current = new Date(start);
  
  while (current <= end) {
    dates.push(new Date(current));
    
    switch (interval) {
      case 'day':
        current.setDate(current.getDate() + 1);
        break;
      case 'week':
        current.setDate(current.getDate() + 7);
        break;
      case 'month':
        current.setMonth(current.getMonth() + 1);
        break;
      default:
        current.setDate(current.getDate() + 1);
    }
  }

  return dates.map(date => formatDate(date, { format: 'iso' }));
};

/**
 * Get business day information
 */
export const getBusinessDayInfo = (date) => {
  const dateObj = new Date(date);
  const dayOfWeek = dateObj.getDay();
  
  return {
    isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    isWeekday: dayOfWeek >= 1 && dayOfWeek <= 5,
    dayName: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
    dayOfWeek
  };
};

/**
 * Format duration in human readable format
 */
export const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return 'Invalid duration';
  }
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 'Invalid duration';
  }
  
  const diffMs = end - start;
  if (diffMs < 0) {
    return 'Invalid duration';
  }
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  if (minutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  
  return `${hours}h ${minutes}m`;
};